import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  AccessManagementOrgUnitCollectionOU,
  AccessManagementPermissionAPIReturnFormats,
  AccessManagementPermissionMap,
  AccessManagementPermissionRecord,
  AccessManagementRolePermissionRecord,
  AccessManagementRoleRecord,
  AccessManagementUserCollectionUser,
} from '@cdw-ae/common-data';
import {
  MatDialog,
  MaterialLibraryModule,
} from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import {
  CdwConfirmationDialogComponent,
  CdwDialogModule,
} from '@cdw-ae/common-ui-components-dialog';
import {
  CdwDrawerModule,
  closeDrawer,
  DrawerNotificationService,
  openDrawer,
} from '@cdw-ae/common-ui-components-drawer';
import {
  CdwDrawerContentModule,
  DrawerContent,
} from '@cdw-ae/common-ui-components-drawer-content';
import {
  CdwDropdownMenuModule,
  CdwDropdownMenuOption,
} from '@cdw-ae/common-ui-components-dropdown-menu';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import * as AccessManagementActions from '../../../../../+state/access-management/access-management.actions';
import { selectSelectedTabIndex } from '../../../../../+state/access-management/access-management.selectors';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { AdministrationComponent } from '../administration.component';
import { DesignationsComponent } from './designations/designations.component';
import { DesignationsDataService } from './designations/designations-data.service';
import { OuCollectionsComponent } from './ou-collections/ou-collections.component';
import { OrgUnitCollectionsDataService } from './ou-collections/ou-collections-data.service';
import { PermissionsDataService } from './permissions/permissions-data.service';
import { RolesComponent } from './roles/roles.component';
import { RolesDataService } from './roles/roles-data.service';
import { UserCollectionCsvUploadDialogComponent } from './user-collections/user-collection-csv-upload-dialog/user-collection-csv-upload-dialog.component';
import { UserCollectionsComponent } from './user-collections/user-collections.component';
import { UserCollectionsDataService } from './user-collections/user-collections-data.service';

@Component({
  selector: 'access-management',
  imports: [
    CommonModule,
    CdwButtonModule,
    CdwIconModule,
    CdwSelectModule,
    MatCardModule,
    MaterialLibraryModule,
    AdministrationComponent,
    ReactiveFormsModule,

    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    CdwCheckboxModule,

    CdwDrawerModule,
    CdwDrawerContentModule,
    CdwDialogModule,
    CdwDropdownMenuModule,

    RolesComponent,
    UserCollectionsComponent,
    OuCollectionsComponent,
    DesignationsComponent,
  ],
  templateUrl: './access-management.component.html',
  styleUrl: './access-management.component.scss',
})
export class AccessManagementComponent implements OnInit {
  @ViewChild('userCollections')
  userCollectionsComponent!: UserCollectionsComponent;
  @ViewChild('roles') rolesComponent!: RolesComponent;
  @ViewChild('ouCollections') ouCollectionsComponent!: OuCollectionsComponent;
  @ViewChild('designations') designationsComponent!: DesignationsComponent;
  constructor(
    private userCollectionService: UserCollectionsDataService,
    private roleDataService: RolesDataService,
    private ouCollectionDataService: OrgUnitCollectionsDataService,
    private designationDataService: DesignationsDataService,
    private permissionsDataService: PermissionsDataService,
    private snackbar: MatSnackBar,
    private appState: Store,
    private router: Router,
    private dialog: MatDialog,
    private drawerNotifications: DrawerNotificationService
  ) {}

  private accessManagementItemId!: number;
  protected currentItemType = '';
  public drawerContent: DrawerContent[] = [];
  public accessManagementDrawerTitle = 'Summary';
  public accessManagementDrawerSubtitle = '';
  public selectedIndex = 0;
  public selectedIndex$!: Observable<number>;

  public readonly tertiaryBlueCta: string = TailwindColors.tertiaryBlueCta;
  public readonly gray50: string = TailwindColors.gray50;
  public readonly gray300: string = TailwindColors.gray300;

  public userCollectionMenuOptions: CdwDropdownMenuOption[] = [
    {
      label: 'Set up a collection manually',
      value: 'manual',
      icon: 'add_circle',
      useMatIcon: false,
      iconColor: this.tertiaryBlueCta,
    },
    {
      label: 'Set up collection(s) via CSV',
      icon: 'arrow_circle_down',
      useMatIcon: true,
      value: 'csv',
      iconColor: this.tertiaryBlueCta,
    },
  ];

  public getDeleteLabel(): string {
    switch (this.currentItemType) {
      case 'role':
        return 'Delete Role';
      case 'userCollection':
        return 'Delete Collection';
      case 'ouCollection':
        return 'Delete Collection';
      case 'designation':
        return 'Delete Designation';
      default:
        return 'Delete';
    }
  }

  public isDeleteDisabled(): boolean {
    // only show delete button when item type matches the current tab
    const tabToTypeMap: Record<number, string> = {
      0: 'role',
      1: 'userCollection',
      2: 'ouCollection',
      3: 'designation',
    };

    const expectedTypeForTab = tabToTypeMap[this.selectedIndex];

    // Disable if current item type doesn't match the selected tab
    // OR if it's a designation or individual orgUnit (never allow delete for these)
    return (
      this.currentItemType !== expectedTypeForTab ||
      this.currentItemType === 'designation' ||
      this.currentItemType === 'orgUnit'
    );
  }

  ngOnInit(): void {
    // Initialize store and load tab from URL
    this.appState.dispatch(AccessManagementActions.initAccessManagement());

    // Subscribe to selected tab from store
    this.selectedIndex$ = this.appState.select(selectSelectedTabIndex);

    // Sync local selectedIndex with store for compatibility with existing code
    this.selectedIndex$.subscribe((index) => {
      this.selectedIndex = index;
    });
  }

  public onTabChange(tabIndex: number) {
    // Dispatch action to update store (which will handle URL update via effects)
    this.appState.dispatch(
      AccessManagementActions.setSelectedTab({ tabIndex })
    );
  }

  public deleteAccessManagementItem(): void {
    const configMap = [
      {
        itemType: 'role',
        canDeleteFn: () =>
          this.roleDataService.canDeleteRole(this.accessManagementItemId ?? 0),
        deleteFn: () =>
          this.roleDataService.deleteRole(this.accessManagementItemId ?? 0),
        onSuccess: () => this.rolesComponent.refreshSearchGrid(),
      },
      {
        itemType: 'user collection',
        canDeleteFn: () =>
          this.userCollectionService.canDeleteUserCollection(
            this.accessManagementItemId ?? 0
          ),
        deleteFn: () =>
          this.userCollectionService.deleteUserCollection(
            this.accessManagementItemId ?? 0
          ),
        onSuccess: () => this.userCollectionsComponent.refreshSearchGrid(),
      },
      {
        itemType: 'OU collection',
        canDeleteFn: () =>
          this.ouCollectionDataService.canDeleteOrgUnitCollection(
            this.accessManagementItemId ?? 0
          ),
        deleteFn: () =>
          this.ouCollectionDataService.deleteOrgUnitCollection(
            this.accessManagementItemId ?? 0
          ),
        onSuccess: () => this.ouCollectionsComponent.refreshSearchGrid(),
      },
      {
        itemType: 'designation',
        canDeleteFn: undefined, // Designations don't have dependencies
        deleteFn: () =>
          this.designationDataService.deleteDesignation(
            this.accessManagementItemId ?? 0
          ),
        onSuccess: () => this.designationsComponent.refreshSearchGrid(),
      },
    ];

    const config = configMap[this.selectedIndex];
    if (!config) return;

    // Check if item can be deleted before showing confirmation dialog
    if (config.canDeleteFn) {
      config
        .canDeleteFn()
        .pipe(take(1))
        .subscribe((canDeleteResponse) => {
          if (!canDeleteResponse.canDelete) {
            // Show error immediately without confirmation dialog
            this.snackbar.openFromComponent(ErrorSnackbarComponent, {
              data:
                canDeleteResponse.message ||
                `Error deleting ${config.itemType}, it is assigned to a designation`,
            });
            return;
          }

          // Item can be deleted, show confirmation dialog
          this.showDeleteConfirmationDialog(config);
        });
    } else {
      // No canDelete check needed, show confirmation dialog directly
      this.showDeleteConfirmationDialog(config);
    }
  }

  private showDeleteConfirmationDialog(config: {
    itemType: string;
    deleteFn: () => Promise<Observable<{ success: boolean }>>;
    onSuccess: () => void;
  }): void {
    // Capitalize first letter of item type for button text
    const capitalizedItemType =
      config.itemType.charAt(0).toUpperCase() + config.itemType.slice(1);

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      data: {
        title: 'Confirmation',
        message: `Are you sure you would like to delete this ${config.itemType}?`,
        type: 'warning',
        layout: 'warning-box',
        confirmText: `Delete ${capitalizedItemType}`,
        cancelText: 'Cancel',
        confirmButtonColor: 'warn',
        hard: true,
      },
      width: '540px',
      maxWidth: '100%',
      disableClose: true,
      hasBackdrop: true,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(
        async (
          result: { confirmed: boolean; dontShowAgain?: boolean } | undefined
        ) => {
          if (!result?.confirmed) return;

          const observable = await config.deleteFn();
          observable.pipe(take(1)).subscribe((response) => {
            if (response.success) {
              this.snackbar.open(
                `${config.itemType.charAt(0).toUpperCase() + config.itemType.slice(1)} deleted successfully.`,
                'OK',
                { duration: 6000 }
              );
              config.onSuccess();
              this.appState.dispatch(
                closeDrawer({ id: 'accessManagementDrawer' })
              );
              this.drawerNotifications.notifyCloseDrawer(
                'accessManagementDrawer'
              );
            } else {
              this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                data: 'Delete operation failed.',
              });
            }
          });
        }
      );
  }

  editAccessManagementItem() {
    switch (this.currentItemType) {
      case 'role':
        return this.router.navigate([
          `administration/access-management/role/edit/${this.accessManagementItemId}`,
        ]);
      case 'userCollection':
        return this.router.navigate([
          `administration/access-management/user-collection/edit/${this.accessManagementItemId}`,
        ]);
      case 'ouCollection':
        return this.router.navigate([
          `administration/access-management/org-unit-collection/edit/${this.accessManagementItemId}`,
        ]);
      case 'designation':
        return this.router.navigate([
          `administration/access-management/designation/edit/${this.accessManagementItemId}`,
        ]);

      default:
        return this.router.navigate([
          `administration/access-management/role/edit/${this.accessManagementItemId}`,
        ]);
    }
  }

  private formatPermissionsByCategory(
    rolePermissions: AccessManagementRolePermissionRecord[],
    allPermissions: AccessManagementPermissionMap
  ): Map<
    string,
    Map<
      string,
      { permissions: string[]; icons: string[]; iconColors: string[] }
    >
  > {
    const onPermissionIds = new Set(
      rolePermissions.map((rp) => rp.permissionId)
    );
    const topLevelCategories = new Map<
      string,
      Map<
        string,
        { permissions: string[]; icons: string[]; iconColors: string[] }
      >
    >();

    // Build a set of IDs that are parents (have children)
    const parentIds = new Set<number>();
    Object.values(allPermissions).forEach(
      (perm: AccessManagementPermissionRecord & { path: string }) => {
        if (perm.parentId) {
          parentIds.add(perm.parentId);
        }
      }
    );

    // Group permissions by 3-level hierarchy: top-level -> mid-level -> permissions
    Object.values(allPermissions).forEach(
      (permission: AccessManagementPermissionRecord & { path: string }) => {
        // Skip if this permission is a parent (has children pointing to it)
        if (parentIds.has(permission.id)) {
          return;
        }

        // Only process leaf permissions (those with a parent)
        if (!(permission.parentId || permission.parent)) {
          return;
        }

        const isOn = onPermissionIds.has(permission.id);
        const icon = isOn ? 'check' : 'close';
        const iconColor = isOn
          ? TailwindColors.chartGreen
          : TailwindColors.cdwRedCorpDark; // Green for check, red for close

        // Get the parent (mid-level category like "Devices", "Device Jobs")
        const parent =
          permission.parent ||
          (permission.parentId ? allPermissions[permission.parentId] : null);

        if (!parent) {
          return; // Skip if no parent found
        }

        // Get the grandparent (top-level category like "Device Management")
        const grandparent =
          parent.parent ||
          (parent.parentId ? allPermissions[parent.parentId] : null);

        let topLevelName = 'General';
        let midLevelName = parent.name;

        if (grandparent) {
          // This is a 3-level hierarchy
          topLevelName = grandparent.name;
        } else {
          // This is a 2-level hierarchy - parent is the top level
          topLevelName = parent.name;
          midLevelName = 'General';
        }

        // Create top-level category if it doesn't exist
        if (!topLevelCategories.has(topLevelName)) {
          topLevelCategories.set(topLevelName, new Map());
        }

        const midLevelCategories = topLevelCategories.get(topLevelName);
        if (!midLevelCategories) {
          return;
        }

        // Create mid-level category if it doesn't exist
        if (!midLevelCategories.has(midLevelName)) {
          midLevelCategories.set(midLevelName, {
            permissions: [],
            icons: [],
            iconColors: [],
          });
        }

        const category = midLevelCategories.get(midLevelName);
        if (!category) {
          return;
        }
        category.permissions.push(permission.name);
        category.icons.push(icon);
        category.iconColors.push(iconColor);
      }
    );

    return topLevelCategories;
  }

  updateDrawerContent(content: unknown) {
    const item = content as {
      id: number;
      name?: string;
      description?: string;
      [key: string]: unknown;
    };
    this.drawerContent = [];
    this.accessManagementItemId = item.id;

    // Configuration for different item types
    const itemTypeConfigs: Record<
      string,
      {
        title: string;
        hasSubtitle: boolean;
        buildContent: (itemContent: typeof item) => void;
        fetchRelatedData?: () => void;
      }
    > = {
      role: {
        title: 'Role summary',
        hasSubtitle: true,
        buildContent: (itemContent) => {
          // Display permissions if available
          const rolePermissions = itemContent.rolePermissions as
            | AccessManagementRolePermissionRecord[]
            | undefined;
          const allPermissions = itemContent.allPermissions as
            | AccessManagementPermissionMap
            | undefined;

          if (rolePermissions && allPermissions) {
            const topLevelCategories = this.formatPermissionsByCategory(
              rolePermissions,
              allPermissions
            );

            // Display 3-level hierarchy
            topLevelCategories.forEach((midLevelCategories, topLevelName) => {
              // Only add top-level if there are mid-level categories with actual permissions
              const hasPermissions = Array.from(
                midLevelCategories.values()
              ).some((cat) => cat.permissions.length > 0);

              if (!hasPermissions) {
                return;
              }

              // Add top-level header (e.g., "Device Management")
              this.drawerContent.push({
                cellTitle: topLevelName.toUpperCase(),
                cellData: [], // Empty data array since we're only showing the title
                hideData: true, // Only show title, not data
                showBorder: false, // No border for top-level headers
                titleFontFamily: 'Source Sans 3',
                titleFontSize: '16px',
                titleFontColor: TailwindColors.gray400,
                titleFontWeight: '700',
                titleLineHeight: '0',
                showBottomBorder: false,
                bottomBorderWidth: '1px',
                bottomBorderStyle: 'solid',
              });

              // Add each mid-level category with its permissions
              midLevelCategories.forEach((categoryData, midLevelName) => {
                if (categoryData.permissions.length === 0) {
                  return; // Skip empty categories
                }

                // Skip "General" mid-level if it exists (means 2-level hierarchy)
                if (midLevelName !== 'General') {
                  // Add mid-level category as section title
                  this.drawerContent.push({
                    cellTitle: midLevelName,
                    cellData: categoryData.permissions,
                    cellIcons: categoryData.icons,
                    cellIconColors: categoryData.iconColors,
                    showBorder: false, // No borders for role permissions
                    titleFontFamily: 'Source Sans 3',
                    titleFontSize: '20px',
                    titleFontColor: TailwindColors.gray200,
                    titleFontWeight: '700',
                    showBottomBorder: true,
                    bottomBorderColor: TailwindColors.gray200,
                    bottomBorderWidth: '1px',
                    bottomBorderStyle: 'solid',
                    dataLineHeight: '1.5',
                  });
                } else {
                  // For 2-level hierarchy, add permissions directly
                  this.drawerContent.push({
                    cellTitle: '',
                    cellData: categoryData.permissions,
                    cellIcons: categoryData.icons,
                    cellIconColors: categoryData.iconColors,
                    showBorder: false, // No borders for role permissions
                    dataFontSize: '16px',
                    dataFontWeight: '400',
                  });
                }
              });
            });
          }
        },
        fetchRelatedData: () => {
          // Fetch role permissions and all permissions
          if (item.id) {
            this.roleDataService
              .getRoleById(item.id)
              .pipe(take(1))
              .subscribe(
                (response: {
                  success: boolean;
                  data: {
                    role: AccessManagementRoleRecord;
                    rolePermissions: AccessManagementRolePermissionRecord[];
                  };
                }) => {
                  if (response.success && response.data) {
                    const { rolePermissions } = response.data;
                    item.rolePermissions = rolePermissions || [];

                    // Fetch all permissions to determine ON/OFF
                    this.permissionsDataService
                      .getPermissions(
                        AccessManagementPermissionAPIReturnFormats.MAP
                      )
                      .pipe(take(1))
                      .subscribe(
                        (
                          allPermissions:
                            | AccessManagementPermissionMap
                            | AccessManagementPermissionRecord[]
                        ) => {
                          item.allPermissions =
                            allPermissions as AccessManagementPermissionMap;

                          // Rebuild the drawer content with the fetched data
                          this.drawerContent = [];
                          this.accessManagementDrawerTitle =
                            itemTypeConfigs.role.title;
                          this.accessManagementDrawerSubtitle = item.name ?? '';
                          itemTypeConfigs.role.buildContent(item);
                        }
                      );
                  }
                }
              );
          }
        },
      },
      userCollection: {
        title: 'User collection summary',
        hasSubtitle: true,
        buildContent: (itemContent) => {
          // Extract user names from userCollectionUsers
          const userNames =
            (
              itemContent.userCollectionUsers as
                | AccessManagementUserCollectionUser[]
                | undefined
            )
              ?.map(
                (
                  userCollectionUser: AccessManagementUserCollectionUser
                ): string => {
                  if (typeof userCollectionUser === 'string') {
                    return userCollectionUser;
                  }
                  // Only access properties that exist on AccessManagementUserCollectionUser
                  return userCollectionUser?.user?.name || '';
                }
              )
              .filter((name: string) => name)
              .sort((a, b) => a.localeCompare(b)) || []; // Sort alphabetically ascending

          // Create icons array with 'account_circle' for each user
          const userIcons = userNames.map(() => 'account_circle');

          this.drawerContent.push({
            cellTitle: '',
            cellData: userNames,
            cellIcons: userIcons,
            displayStyle: 'bubble',
            showBorder: false,
            rowGap: '20px',
          });
        },
        fetchRelatedData: () => {
          // If userCollectionUsers is not present, fetch it
          if (!item.userCollectionUsers && item.id) {
            this.userCollectionService
              .getUserCollectionById(item.id)
              .pipe(take(1))
              .subscribe((response) => {
                if (response.success && response.data) {
                  item.userCollectionUsers =
                    response.data.userCollectionUsers || [];
                  // Rebuild the drawer content with the fetched data
                  this.drawerContent = [];
                  this.accessManagementDrawerTitle =
                    itemTypeConfigs.userCollection.title;
                  this.accessManagementDrawerSubtitle = item.name ?? '';
                  itemTypeConfigs.userCollection.buildContent(item);
                }
              });
          }
        },
      },
      ouCollection: {
        title: 'OU collection summary',
        hasSubtitle: true,
        buildContent: (itemContent) => {
          // Extract OU paths from ouCollectionOus
          const ouPaths =
            (
              itemContent.ouCollectionOus as
                | AccessManagementOrgUnitCollectionOU[]
                | undefined
            )
              ?.map(
                (
                  ouCollectionOu: AccessManagementOrgUnitCollectionOU
                ): string => {
                  if (typeof ouCollectionOu === 'string') {
                    return ouCollectionOu;
                  }
                  // Only access properties that exist on AccessManagementOrgUnitCollectionOU
                  return ouCollectionOu?.orgUnit?.value || '';
                }
              )
              .filter((path: string) => path)
              .sort((a, b) => a.localeCompare(b)) || []; // Sort alphabetically ascending

          this.drawerContent.push({
            cellTitle: '',
            cellData: ouPaths,
            titleFontFamily: 'Source Sans 3',
            titleFontSize: '18px',
            titleFontColor: TailwindColors.gray400,
            titleFontWeight: '700',
            dataFontSize: '14px',
            dataFontWeight: '400',
            dataLineHeight: '1',
          });
        },
        fetchRelatedData: () => {
          // If ouCollectionOus is not present, fetch it
          if (!item.ouCollectionOus && item.id) {
            this.ouCollectionDataService
              .getOrgUnitCollectionById(item.id)
              .pipe(take(1))
              .subscribe((response) => {
                if (response.success && response.data) {
                  item.ouCollectionOus = response.data.ouCollectionOus || [];
                  // Rebuild the drawer content with the fetched data
                  this.drawerContent = [];
                  this.accessManagementDrawerTitle =
                    itemTypeConfigs.ouCollection.title;
                  this.accessManagementDrawerSubtitle = item.name ?? '';
                  itemTypeConfigs.ouCollection.buildContent(item);
                }
              });
          }
        },
      },
      designation: {
        title: 'Designation Summary',
        hasSubtitle: false,
        buildContent: (itemContent) => {
          // Define field mappings for designation items
          const fieldMappings = [
            {
              field: 'role',
              title: 'Role',
              accessor: (item: { name?: string }) => item?.name,
            },
            {
              field: 'user',
              title: 'User',
              accessor: (item: { name?: string }) => item?.name,
            },
            {
              field: 'userCollection',
              title: 'User Collection',
              accessor: (item: { name?: string }) => item?.name,
            },
            {
              field: 'orgUnit',
              title: 'OU',
              accessor: (item: { path?: string }) => item?.path,
            },
            {
              field: 'orgUnitCollection',
              title: 'OU Collection',
              accessor: (item: { name?: string }) => item?.name,
            },
          ];

          // Add only fields that exist in the content
          fieldMappings.forEach(({ field, title, accessor }) => {
            if (itemContent[field]) {
              const value = accessor(itemContent[field]);
              if (value) {
                this.drawerContent.push({
                  cellData: [value],
                  cellTitle: title,
                });
              }
            }
          });
        },
      },
      orgUnit: {
        title: 'OU summary',
        hasSubtitle: true,
        buildContent: (itemContent) => {
          // Display the full OU path in the content area
          const ouPath = itemContent.ouPath as string | undefined;
          if (ouPath) {
            this.drawerContent.push({
              cellTitle: 'OU Path',
              cellData: [ouPath],
              titleFontFamily: 'Source Sans 3',
              titleFontSize: '18px',
              titleFontColor: TailwindColors.gray400,
              titleFontWeight: '700',
              dataFontSize: '14px',
              dataFontWeight: '400',
              dataLineHeight: '1.5',
            });
          }
        },
      },
    };

    // Determine the type
    const type =
      (item._itemType as string) ||
      (['role', 'userCollection', 'ouCollection', 'designation'][
        this.selectedIndex
      ] as string) ||
      'role';
    const config = itemTypeConfigs[type];

    // Store the current item type for edit navigation
    this.currentItemType = type;

    if (config) {
      // Set title and subtitle
      this.accessManagementDrawerTitle = config.title;

      // For orgUnit type, use ouPath as subtitle; otherwise use name
      if (type === 'orgUnit') {
        const ouPath = item.ouPath as string | undefined;
        const leaf = ouPath?.split('/').filter(Boolean).at(-1) ?? ouPath ?? '';
        this.accessManagementDrawerSubtitle = config.hasSubtitle ? leaf : '';
      } else {
        this.accessManagementDrawerSubtitle = config.hasSubtitle
          ? (item.name ?? '')
          : '';
      }

      // Build drawer content
      config.buildContent(item);

      // Fetch related data if needed and function is defined
      if (config.fetchRelatedData) {
        config.fetchRelatedData();
      }
    }

    this.appState.dispatch(openDrawer({ id: 'accessManagementDrawer' }));
    this.drawerNotifications.notifyOpenDrawer('accessManagementDrawer');
  }

  public getNewButtonText(): string {
    switch (this.selectedIndex) {
      case 0:
        return 'Add new role';
      case 1:
        return 'Add new user collection(s)';
      case 2:
        return 'Add new OU collection';
      case 3:
        return 'Add new designation';
      default:
        return 'Add new role';
    }
  }

  public async newButtonClick(): Promise<void> {
    switch (this.selectedIndex) {
      case 0:
        await this.router.navigate([
          '/administration/access-management/role/edit/new',
        ]);
        break;
      case 1:
        await this.router.navigate([
          '/administration/access-management/user-collection/edit/new',
        ]);
        break;
      case 2:
        await this.router.navigate([
          'administration/access-management/org-unit-collection/edit/new',
        ]);
        break;
      case 3:
        await this.router.navigate([
          '/administration/access-management/designation/edit/new',
        ]);
        break;
      default:
        await this.router.navigate([
          '/administration/access-management/role/edit/new',
        ]);
        break;
    }
  }

  public async onSetupManually(): Promise<void> {
    await this.router.navigate([
      '/administration/access-management/user-collection/edit/new',
    ]);
  }

  public onSetupViaCSV(): void {
    const dialogRef = this.dialog.open(UserCollectionCsvUploadDialogComponent, {
      data: {
        infoMessage:
          'Download a CSV template to get started with the correct format.',
      },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: { success: boolean }) => {
        if (result?.success) {
          this.snackbar.open('User collections imported successfully!', 'OK', {
            duration: 6000,
          });
          // Refresh the user collections grid
          this.userCollectionsComponent.refreshSearchGrid();
        }
      });
  }

  public onUserCollectionMenuSelect(option: CdwDropdownMenuOption): void {
    if (option.value === 'manual') {
      this.onSetupManually();
    } else if (option.value === 'csv') {
      this.onSetupViaCSV();
    }
  }
}
